import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { NavContext } from "@ionic/react";
import type { NavContextState } from "@ionic/react";
import { useRouteRestoration } from "../hooks/useRouteRestoration";

/**
 * Fornisce NavContext con navigazione client-side via React Router.
 * Senza IonReactRouter, Ionic usa window.location (full reload).
 * Questo bridge usa useNavigate per navigazione SPA senza reload.
 */
export const NavContextBridge: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const navigate = useNavigate();
    const location = useLocation();

    // Restore last route after iOS process-kill reloads.
    useRouteRestoration();

    const routeInfo = {
        id: `route-${location.pathname}${location.search}`,
        pathname: location.pathname,
        search: location.search,
    };

    const value: NavContextState = {
        getIonRedirect: () => undefined,
        getIonRoute: () => undefined,
        getPageManager: () => undefined,
        getStackManager: () => undefined,
        goBack: (route?: string | { pathname: string; search?: string }) => {
            if (typeof route === "string") {
                navigate(route);
            } else {
                window.history.back();
            }
        },
        navigate: (path: string) => navigate(path),
        hasIonicRouter: () => false,
        routeInfo,
        setCurrentTab: () => undefined,
        changeTab: (_tab: string, path: string) => navigate(path),
        resetTab: (_tab: string, path: string) =>
            navigate(path, { replace: true }),
    };

    return (
        <NavContext.Provider value={value}>{children}</NavContext.Provider>
    );
};
